import { useState, useEffect, useRef, useCallback } from 'react';
import { dashboardsService, DashboardCreationStatus, ApiClientError } from '../../../api';

interface UseCreationPollingOptions {
  jobId: string;
  interval?: number; // milliseconds, default 1000
  onComplete?: (status: DashboardCreationStatus) => void;
  onError?: (error: string) => void;
  onCancelled?: () => void;
}

interface UseCreationPollingResult {
  status: DashboardCreationStatus | null;
  isPolling: boolean;
  error: string | null;
}

/**
 * Custom hook for polling dashboard creation status.
 * Polls every `interval` ms until completion, failure, or cancellation.
 * Automatically cleans up on unmount.
 */
export const useCreationPolling = ({
  jobId,
  interval = 1000,
  onComplete,
  onError,
  onCancelled,
}: UseCreationPollingOptions): UseCreationPollingResult => {
  const [status, setStatus] = useState<DashboardCreationStatus | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store callbacks in refs to avoid dependency issues that cause multiple intervals
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const onCancelledRef = useRef(onCancelled);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
    onCancelledRef.current = onCancelled;
  }, [onComplete, onError, onCancelled]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const currentStatus = await dashboardsService.getJobStatus(jobId);

        if (!isMounted) return;

        setStatus(currentStatus);
        setError(null);

        if (currentStatus.state === 'completed') {
          stopPolling();
          onCompleteRef.current?.(currentStatus);
        } else if (currentStatus.state === 'failed') {
          stopPolling();
          const errorMsg = currentStatus.error || 'Dashboard creation failed';
          setError(errorMsg);
          onErrorRef.current?.(errorMsg);
        } else if (currentStatus.state === 'cancelled') {
          stopPolling();
          onCancelledRef.current?.();
        }
      } catch (err) {
        if (!isMounted) return;

        const errorMessage =
          err instanceof ApiClientError
            ? err.error
            : 'Failed to fetch dashboard status';

        setError(errorMessage);
        stopPolling();
        onErrorRef.current?.(errorMessage);
      }
    };

    // Initial fetch
    fetchStatus();

    // Set up polling interval
    intervalRef.current = setInterval(fetchStatus, interval);

    // Cleanup
    return () => {
      isMounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobId, interval, stopPolling]);

  return { status, isPolling, error };
};